import { useState, useEffect } from "react";
import { Typography, Spin, message } from "antd";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import DocUpdateIcon from "../../assets/DocUpdateIcon.svg";
import WPFooter from "../../components/WPFooter/WPFooter";
import ShippingAddressModal from "./ShippingAddressModal";
import {
  getFormData,
  getRequestStatus,
  getRequestIdFromUrl,
  resolveProducts,
  Address as ApiAddress,
  FormData,
} from "../../api/samplesIntegrationApi";
import "./AddressSelection.css";

interface Address {
  id: string;
  addressId: number;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  licNumber: string;
}

const AddressSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [requestID, setRequestID] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const requestIdFromUrl =
        getRequestIdFromUrl() || searchParams.get("requestID");

      if (!requestIdFromUrl) {
        setError("Invalid request. No request ID provided.");
        setIsLoading(false);
        return;
      }

      setRequestID(requestIdFromUrl);

      try {
        const status = await getRequestStatus(requestIdFromUrl);
        if (status.otpStatus !== "verified") {
          navigate(`/verify-phone?requestID=${requestIdFromUrl}`);
          return;
        }
        if (status.isClosed) {
          navigate("/form-unavailable", {
            state: {
              reason:
                status.formStatus === "submitted" ? "submitted" : "closed",
            },
          });
          return;
        }

        const response = await getFormData(requestIdFromUrl);
        setFormData(response.formData);

        const transformedAddresses: Address[] = (
          response.formData.addresses || []
        ).map((addr: ApiAddress, index: number) => ({
          id: String(addr.addressId || `addr_${index}`),
          addressId: addr.addressId,
          street: addr.street + (addr.suite ? `, ${addr.suite}` : ""),
          city: addr.city,
          state: addr.state,
          zipCode: addr.zipCode,
          licNumber: addr.stateLicense?.licNumber || "",
        }));

        setAddresses(transformedAddresses);
        setModalOpen(true);
      } catch (err: any) {
        console.error("Failed to fetch form data:", err);
        setError(err.message || "Failed to load addresses");
        // If form not found or expired, redirect to unavailable page
        if (
          err.message?.includes("not found") ||
          err.message?.includes("expired")
        ) {
          navigate("/form-unavailable", { state: { reason: "expired" } });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, searchParams]);

  const handleConfirm = (address: Address) => {
    setModalOpen(false);
    navigate(`/sample-request-form?requestID=${requestID}`, {
      state: {
        requestID,
        selectedAddress: address,
        formData,
        generalInfo: {
          hcpName: formData?.hcp
            ? `${formData.hcp.firstName} ${formData.hcp.lastName}`
            : "",
          designation: formData?.hcp?.designation || "",
          stateLicenseNumber: address.licNumber,
          shippingStreet: address.street,
          shippingCity: `${address.city}, ${address.state} ${address.zipCode}`,
        },
        requestNumber: formData?.requestInfo?.requestNumber || "",
        orderItems: formData?.decision
          ? resolveProducts(formData.decision)
          : [],
      },
    });
  };

  const handleClose = () => {
    setModalOpen(false);
    // Go back to previous page or stay on this page
    navigate(-1);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="address-selection-container">
        <header className="address-selection-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="address-selection-logo"
            />
          </Link>
        </header>
        <main className="address-selection-main">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
            }}
          >
            <Spin size="large" />
            <Typography.Text style={{ marginTop: 16, color: "#6b7280" }}>
              Loading addresses...
            </Typography.Text>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="address-selection-container">
        <header className="address-selection-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="address-selection-logo"
            />
          </Link>
        </header>
        <main className="address-selection-main">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
            }}
          >
            <Typography.Text style={{ color: "#ef4444", fontSize: 16 }}>
              {error}
            </Typography.Text>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="address-selection-container">
      {/* Header */}
      <header className="address-selection-header">
        <Link to="https://www.docupdate.io/">
          <img
            src={DocUpdateIcon}
            alt="DocUpdate"
            className="address-selection-logo"
          />
        </Link>
      </header>

      {/* Main Content - Blurred background */}
      <main className="address-selection-main">
        <div className="address-selection-blur">
          <Typography.Title className="address-selection-title">
            Sample Order Form
          </Typography.Title>

          {/* Placeholder content that appears blurred behind modal */}
          <div className="address-selection-placeholder">
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-cards">
                <div className="placeholder-card"></div>
                <div className="placeholder-card"></div>
                <div className="placeholder-card"></div>
              </div>
            </div>
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-table"></div>
            </div>
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-content"></div>
            </div>
          </div>
        </div>
      </main>

      <WPFooter />

      {/* Shipping Address Modal */}
      <ShippingAddressModal
        open={modalOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        addresses={addresses}
      />
    </div>
  );
};

export default AddressSelection;
